<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.

 UI: redesigned Account page (specs/2026-06-11-account-page-redesign) — full-bleed two-pane
 layout (left tab rail + content) matching the Image Library / Storage page, so the page
 uses the full width instead of a centered card. Password remains Keycloak-managed
 (the Update password button redirects to the secure sign-in page). -->

<template>
<div class="acct">
  <b-loading :is-full-page="false" :active="loading" />

  <!-- left tab rail -->
  <aside v-if="!loading" class="acct-rail">
    <p class="acct-rail-title">{{ $t('account') }}</p>
    <nav class="acct-rail-nav">
      <button class="acct-rail-item" :class="{active: activeTab === 'profile'}" @click="activeTab = 'profile'">
        <span class="icon"><i class="fas fa-user"></i></span><span>{{ $t('profile') }}</span>
      </button>
      <button class="acct-rail-item" :class="{active: activeTab === 'password'}" @click="activeTab = 'password'">
        <span class="icon"><i class="fas fa-lock"></i></span><span>{{ $t('password') }}</span>
      </button>
      <button class="acct-rail-item" :class="{active: activeTab === 'apikeys'}" @click="activeTab = 'apikeys'">
        <span class="icon"><i class="fas fa-key"></i></span><span>{{ $t('api-keys') }}</span>
      </button>
    </nav>

    <!-- identity card pinned to the foot of the rail -->
    <div class="acct-rail-foot">
      <div class="acct-id-card">
        <span class="acct-avatar">{{ initials }}</span>
        <div class="acct-id-text">
          <div class="acct-id-name">{{ updatedAccount.firstName }} {{ updatedAccount.lastName }}</div>
          <div class="acct-id-sub">@{{ updatedAccount.username }}</div>
        </div>
      </div>
    </div>
  </aside>

  <!-- content -->
  <section v-if="!loading" class="acct-main">
      <!-- PROFILE -->
      <div v-show="activeTab === 'profile'" class="acct-view">
        <header class="acct-head">
          <div>
            <h1 class="acct-title">{{ $t('profile') }}</h1>
            <p class="acct-subtitle">{{ $t('profile-subtitle') }}</p>
          </div>
          <span class="tag acct-role" :class="role.class">{{ $t(role.label) }}</span>
        </header>

        <form @submit.prevent="editDetails()" data-vv-scope="profile">
          <div class="acct-grid">
            <b-field
              :label="$t('firstname')"
              :type="{'is-danger': errors.has('profile.firstname')}"
              :message="errors.first('profile.firstname')"
            >
              <b-input v-model="updatedAccount.firstName" name="firstname" v-validate="'required'" />
            </b-field>

            <b-field
              :label="$t('lastname')"
              :type="{'is-danger': errors.has('profile.lastname')}"
              :message="errors.first('profile.lastname')"
            >
              <b-input v-model="updatedAccount.lastName" name="lastname" v-validate="'required'" />
            </b-field>

            <b-field
              class="acct-col-2"
              :label="$t('email')"
              :type="{'is-danger': errors.has('profile.email')}"
              :message="errors.first('profile.email')"
            >
              <b-input v-model="updatedAccount.email" name="email" v-validate="'required|email'" icon="envelope" />
            </b-field>

            <b-field :label="$t('language')">
              <b-select v-model="updatedAccount.locale" expanded>
                <option v-for="{value, name} in languages" :key="value" :value="value">{{ name }}</option>
              </b-select>
            </b-field>

            <b-field :label="$t('username')" :message="$t('username-cannot-change')">
              <b-input :value="updatedAccount.username" disabled />
            </b-field>
          </div>

          <hr class="acct-hr">

          <div class="acct-toggle-row">
            <div>
              <div class="acct-toggle-label">{{ $t('developer-mode') }}</div>
              <div class="acct-toggle-desc">{{ $t('developer-mode-description') }}</div>
            </div>
            <b-switch v-model="updatedAccount.isDeveloper" />
          </div>

          <div class="acct-footer">
            <button class="button" type="button" @click="cancelProfile()">{{ $t('button-cancel') }}</button>
            <button class="button is-link" :disabled="errors.any('profile')">{{ $t('save-changes') }}</button>
          </div>
        </form>
      </div>

      <!-- PASSWORD -->
      <div v-show="activeTab === 'password'" class="acct-view">
        <header class="acct-head">
          <div>
            <h1 class="acct-title">{{ $t('password') }}</h1>
            <p class="acct-subtitle">{{ $t('password-subtitle') }}</p>
          </div>
        </header>

        <b-message
          v-if="credentialsError || !passwordCredentials"
          type="is-danger" has-icon icon-size="is-small" size="is-small"
        >
          {{ $t('failed-to-retrieve-credentials') }}
        </b-message>
        <template v-else>
          <div class="acct-updated" v-if="currentPasswordInfo">
            <i class="far fa-clock"></i>
            {{ $t('last-updated-on') }} {{ Number(currentPasswordInfo.createdDate) | moment('ll LT') }}
          </div>
          <div class="acct-updated" v-else><em>{{ $t('no-password') }}</em></div>

          <p class="acct-note">{{ $t('password-managed-note') }}</p>

          <div class="acct-footer">
            <button class="button is-link" v-if="passwordCredentials.updateAction" @click="updatePassword()">
              {{ $t('update-password') }}
            </button>
          </div>
        </template>
      </div>

      <!-- API KEYS -->
      <div v-show="activeTab === 'apikeys'" class="acct-view">
        <header class="acct-head">
          <div>
            <h1 class="acct-title">{{ $t('api-keys') }}</h1>
            <p class="acct-subtitle">{{ $t('api-keys-subtitle') }}</p>
          </div>
        </header>

        <b-message v-if="apiKeysError" type="is-danger" has-icon icon-size="is-small" size="is-small">
          {{ $t('failed-to-retrieve-api-keys') }}
        </b-message>
        <template v-else-if="apiKeys">
          <b-message type="is-info" size="is-small" has-icon icon-size="is-small">
            {{ $t('private-key-warning') }}
          </b-message>

          <div class="acct-key">
            <label class="acct-key-label">{{ $t('public-key') }}</label>
            <div class="acct-key-row">
              <input class="acct-key-input" :value="apiKeys.primaryKey" readonly @focus="$event.target.select()">
              <button class="button acct-key-btn" type="button" @click="copy(apiKeys.primaryKey)">
                <span class="icon"><i class="far fa-clipboard"></i></span><span>{{ $t('button-copy') }}</span>
              </button>
            </div>
          </div>

          <div class="acct-key">
            <label class="acct-key-label">{{ $t('private-key') }}</label>
            <div class="acct-key-row">
              <input class="acct-key-input" :value="revealPrivateKey ? apiKeys.secondaryKey : maskedPrivateKey" readonly @focus="$event.target.select()">
              <button class="button acct-key-btn" type="button" @click="revealPrivateKey = !revealPrivateKey">
                <span class="icon"><i class="far" :class="revealPrivateKey ? 'fa-eye-slash' : 'fa-eye'"></i></span>
                <span>{{ revealPrivateKey ? $t('hide') : $t('reveal') }}</span>
              </button>
              <button class="button acct-key-btn" type="button" @click="copy(apiKeys.secondaryKey)">
                <span class="icon"><i class="far fa-clipboard"></i></span><span>{{ $t('button-copy') }}</span>
              </button>
            </div>
          </div>

          <div class="acct-footer">
            <button class="button is-danger is-light" type="button" @click="regenerateKeys()">
              <span class="icon"><i class="fas fa-sync-alt"></i></span><span>{{ $t('regenerate-api-keys') }}</span>
            </button>
          </div>
        </template>
      </div>
  </section>
</div>
</template>

<script>
import {get} from '@/utils/store-helpers';
import {changeLanguageMixin} from '@/lang.js';
import {MyAccount, User} from '@/api';
import {rolesMapping} from '@/utils/role-utils';
import copyToClipboard from 'copy-to-clipboard';

export default {
  // eslint-disable-next-line
  name: 'Account',
  $_veeValidate: {validator: 'new'},
  mixins: [changeLanguageMixin],
  data() {
    return {
      activeTab: 'profile',
      revealPrivateKey: false,
      updatedAccount: this.$store.state.currentUser.account.clone(),
      credentials: null,
      credentialsError: false,
      apiKeys: null,
      apiKeysError: null,
      loading: true,
      languages: [
        {value: 'en', name: 'English'},
        {value: 'fr', name: 'Français'},
        {value: 'es', name: 'Español'},
        {value: 'nl', name: 'Nederlands'},
      ],
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    currentAccount: get('currentUser/account'),
    initials() {
      const f = (this.updatedAccount.firstName || '').trim();
      const l = (this.updatedAccount.lastName || '').trim();
      const ini = (f.charAt(0) + l.charAt(0)) || (this.updatedAccount.username || '?').charAt(0);
      return ini.toUpperCase();
    },
    role() {
      // Guest > User > Admin
      let key = this.$keycloak.hasResourceRole('ADMIN') ? 'ROLE_ADMIN' : this.$keycloak.hasResourceRole('USER') ? 'ROLE_USER' : 'ROLE_GUEST';
      return rolesMapping[key];
    },
    passwordCredentials() {
      return this.credentials ? this.credentials.find(c => c.type === 'password') : null;
    },
    currentPasswordInfo() {
      if (this.passwordCredentials
        && this.passwordCredentials.userCredentialMetadatas
        && this.passwordCredentials.userCredentialMetadatas.length === 1) {
        return this.passwordCredentials.userCredentialMetadatas[0].credential;
      }
      return null;
    },
    maskedPrivateKey() {
      const len = (this.apiKeys && this.apiKeys.secondaryKey) ? this.apiKeys.secondaryKey.length : 32;
      return '•'.repeat(Math.min(len, 44));
    }
  },
  methods: {
    async editDetails() {
      let result = await this.$validator.validateAll('profile');
      if (!result) {
        return;
      }

      try {
        await this.$store.dispatch('currentUser/updateAccount', this.updatedAccount);
        this.changeLanguage(this.currentAccount.locale);
        this.$notify({type: 'success', text: this.$t('notif-success-user-details-saved')});
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-user-details-not-saved')});
      }
    },

    cancelProfile() {
      this.updatedAccount = this.$store.state.currentUser.account.clone();
      this.$validator.reset();
    },

    updatePassword() {
      this.$keycloak.login({action: this.passwordCredentials.updateAction});
    },

    copy(value) {
      copyToClipboard(value);
      this.$notify({type: 'success', text: this.$t('notif-success-key-copied')});
    },

    async regenerateKeys() {
      try {
        this.apiKeys = await User.regenerateKeys();
        this.revealPrivateKey = false;
        this.$notify({type: 'success', text: this.$t('notif-success-keys-regenerated')});
      } catch (err) {
        this.$notify({type: 'error', text: this.$t('notif-error-keys-not-regenerated')});
      }
    }
  },
  async created() {
    try {
      this.credentials = await MyAccount.fetchCredentials();
    } catch (error) {
      this.credentialsError = true;
    }
    try {
      this.apiKeys = await User.fetchCurrentUserKeys();
    } catch (error) {
      this.apiKeysError = true;
    }
    this.loading = false;
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/colors.scss';

/* design tokens — shared with the Image Library / Storage page for a consistent look */
$ink: #0f172a;        // headings
$ink-2: #334155;      // labels
$muted: #64748b;
$muted-2: #94a3b8;
$line: #e6eaf0;
$line-2: #eef1f6;
$brand: $primary;     // ZAYA blue
$brand-ink: #1a5fc7;
$brand-tint: #eef4ff;

/* full-bleed white page split into a left rail + content pane (mirrors .imglib).
   Fixed height (viewport − navbar) so the rail stays put and the content scrolls
   internally — no centered card, no large symmetric white margins. */
.acct {
  display: flex;
  align-items: stretch;
  background: #fff;
  height: 100%;
  padding: 0;
  position: relative;

  ::v-deep .fas, ::v-deep .far { padding-right: 0; }
}

/* ---------------------------------------------------------------- left rail */
.acct-rail {
  width: 256px;
  flex: 0 0 256px;
  border-right: 1px solid $line;
  padding: 26px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  background: linear-gradient(180deg, #fcfdff, #ffffff 120px);
}
.acct-rail-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: $muted-2;
  padding: 2px 12px 10px;
}
.acct-rail-nav {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.acct-rail-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  font-size: 14.5px;
  font-weight: 600;
  color: $ink-2;
  text-align: left;
  transition: 0.14s;

  .icon { color: $muted; height: auto; width: auto; margin: 0; }
  &:hover { background: #f4f7fb; }
  &.active {
    background: $brand-tint;
    color: $brand-ink;
    .icon { color: $brand; }
  }
}

/* identity card pinned to the foot of the rail */
.acct-rail-foot {
  margin-top: auto;
  padding: 14px 12px 4px;
}
.acct-id-card {
  display: flex;
  align-items: center;
  gap: 11px;
  border: 1px solid $line;
  border-radius: 12px;
  padding: 11px 12px;
  background: #fff;
}
.acct-avatar {
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: $brand;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
}
.acct-id-text { min-width: 0; }
.acct-id-name {
  font-size: 13.5px;
  font-weight: 700;
  color: $ink;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.acct-id-sub { font-size: 11.5px; color: $muted-2; }

/* ------------------------------------------------------------------ content */
.acct-main {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
}
/* left-aligned content (no centering) — fills toward the right instead of sitting
   in a narrow centered column. Wide enough for a comfortable two-column form. */
.acct-view {
  margin: 0;
  padding: 34px 40px 56px;
  max-width: 1040px;
}

.acct-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 26px;
}
.acct-title {
  /* override the global h1 (centered, uppercase, padded) — design is left/sentence-case */
  font-size: 27px;
  font-weight: 800;
  letter-spacing: -0.02em;
  text-transform: none;
  text-align: left;
  padding: 0;
  color: $ink;
  line-height: 1.15;
  margin: 0 0 6px;
}
.acct-subtitle {
  margin: 0;
  font-size: 14.5px;
  color: $muted;
}
.acct-role {
  flex: 0 0 auto;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 10px;
}

/* form */
.acct-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 22px;

  .acct-col-2 { grid-column: 1 / -1; }
  @media screen and (max-width: 640px) { grid-template-columns: 1fr; }
}
.acct-view ::v-deep .label {
  font-size: 13px;
  font-weight: 700;
  color: $ink-2;
  margin-bottom: 8px;
}
.acct-view ::v-deep .input,
.acct-view ::v-deep .select select {
  height: 46px;
  border: 1px solid $line;
  border-radius: 11px;
  font-size: 14.5px;
  box-shadow: none;
  color: $ink;
}
.acct-view ::v-deep .select:not(.is-multiple):not(.is-loading)::after {
  border-color: $muted;
}
.acct-view ::v-deep .input:focus,
.acct-view ::v-deep .select select:focus {
  border-color: $brand;
  box-shadow: 0 0 0 3px rgba(31, 111, 229, 0.13);
}
.acct-view ::v-deep .input[disabled] { background: #f7f9fc; color: $muted; }
.acct-view ::v-deep .help { color: $muted-2; }

.acct-hr { border: 0; border-top: 1px solid $line-2; margin: 24px 0; }

.acct-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.acct-toggle-label { font-weight: 700; color: $ink; }
.acct-toggle-desc { font-size: 0.85rem; color: $muted; }

.acct-updated {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.85rem;
  color: $muted;
  background: #f4f7fb;
  border-radius: 8px;
  padding: 6px 12px;
  margin-bottom: 0.9rem;
}
.acct-note { color: $muted; font-size: 0.9rem; margin-bottom: 1.1rem; }

.acct-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 1.4rem;

  .button {
    height: 44px;
    padding: 0 20px;
    border-radius: 10px;
    font-weight: 700;
  }
  .button.is-link {
    background: $brand;
    border-color: $brand;
    &:hover { background: $brand-ink; border-color: $brand-ink; }
  }
}

/* API key rows — aligned, monospace, responsive (buttons wrap under on narrow screens) */
.acct-key { margin-bottom: 1.1rem; }
.acct-key-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
  color: $ink-2;
  margin-bottom: 6px;
}
.acct-key-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: stretch;
}
.acct-key-input {
  flex: 1 1 280px;
  min-width: 0;
  height: 44px;
  padding: 0 12px;
  border: 1px solid $line;
  border-radius: 10px;
  background: #fff;
  font-family: 'SFMono-Regular', Menlo, Consolas, 'Liberation Mono', monospace;
  font-size: 0.85rem;
  letter-spacing: 0.02em;
  color: $ink;
}
.acct-key-input:focus { outline: none; border-color: $brand; box-shadow: 0 0 0 3px rgba(31, 111, 229, 0.13); }
.acct-key-btn { flex: 0 0 auto; height: 44px; white-space: nowrap; border-radius: 10px; }

/* -------------------------------------------------------------- responsive */
@media screen and (max-width: 768px) {
  .acct { flex-direction: column; height: auto; }
  .acct-rail {
    flex-basis: auto;
    width: 100%;
    border-right: 0;
    border-bottom: 1px solid $line;
    padding: 16px;
    overflow-y: visible;
  }
  .acct-rail-nav { flex-direction: row; flex-wrap: wrap; }
  .acct-rail-item { width: auto; }
  .acct-rail-foot { display: none; }
  .acct-main { overflow-y: visible; }
  .acct-view { padding: 24px 18px 40px; }
  .acct-head { flex-direction: column; }
}
</style>
